"use client";

import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@ds/lib/utils";

/**
 * Badge — Capture Sheet field 12.12.
 *
 * The rule read out of the evidence: a BENEFIT gets colour, a DISCLOSURE gets
 * silence. `disclosure` therefore has no container and no colour at all — that
 * is the design, not an oversight.
 *
 * `brand` is the one place the decorative pink is allowed into a component, and
 * only because a badge is non-interactive. There is deliberately no brand
 * variant on Button (sheet 2.4 / 21.5): if pink is unreachable from an
 * interactive variant, nobody can ship a pink button by accident.
 */
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap [&_svg]:size-3 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        neutral: "rounded-pill bg-muted px-2.5 py-1 text-legal text-muted-foreground",
        advantage:
          "rounded-pill bg-advantage px-2.5 py-1 text-legal font-medium text-advantage-foreground",
        brand:
          "rounded-pill px-2.5 py-1 text-legal font-medium bg-[var(--ds-brand)] text-[var(--ds-brand-foreground)]",
        /** Text-only by rule — gain is never a fill (sheet 3.6). */
        gain: "text-support font-medium text-gain",
        /** No container, no colour. Promoted / sponsored labelling (sheet 3.8). */
        disclosure: "text-support text-muted-foreground",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

function Badge({
  className,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    render,
    props: { ...props, "data-slot": "badge", className: cn(badgeVariants({ variant, className })) },
  });
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
